import { TYPE_META, PRIORITY_META, formatDue, daysUntil, STATUS_LABELS } from "@/lib/utils";
import { useData } from "@/lib/store";
import { Check, MoreHorizontal } from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useState } from "react";
import PaymentConfirmDialog from "./PaymentConfirmDialog";

export default function ItemCard({ item, compact = false }) {
  const { spaces, updateItem } = useData();
  const space = spaces.find((s) => s.id === item.space_id);
  const type = TYPE_META[item.type];
  const prio = PRIORITY_META[item.priority];
  const dd = daysUntil(item.due_date);
  const overdue = dd !== null && dd < 0 && item.status !== "done";
  const done = item.status === "done";
  const [payOpen, setPayOpen] = useState(false);

  const isPayable = (item.type === "bill" || item.type === "renewal") && !!item.linked_recurring_id;

  const toggle = () => {
    if (done) { updateItem(item.id, { status: "todo" }); return; }
    if (isPayable) { setPayOpen(true); return; }
    updateItem(item.id, { status: "done" });
  };

  return (
    <div
      data-testid={`item-card-${item.id}`}
      className={cn(
        "group bg-surface rounded-[16px] p-4 border border-outline-variant hover:border-primary/40 hover:shadow-sm transition-[border-color,box-shadow] duration-200 ease-md",
        done && "opacity-70"
      )}
    >
      <div className="flex items-start gap-3">
        <button
          data-testid={`item-toggle-${item.id}`}
          onClick={toggle}
          className={cn(
            "mt-0.5 shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors duration-200 ease-md",
            done ? "bg-primary border-primary text-primary-foreground" : "border-outline hover:border-primary"
          )}
          aria-label="Toggle done"
        >
          {done && <Check className="w-3 h-3" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className={cn("text-sm font-medium text-surface-on leading-snug", done && "line-through")}>{item.title}</div>
          {!compact && (
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <span className={cn("text-[10px] font-medium tracking-wider uppercase px-2 py-0.5 rounded-full", type.color)}>
                {type.label}
              </span>
              {space && (
                <span className="text-xs text-surface-onVariant inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: space.color }} />
                  {space.name}
                </span>
              )}
              {item.due_date && (
                <span className={cn("text-xs", overdue ? "text-danger font-medium" : "text-surface-onVariant")}>
                  {formatDue(item.due_date)}
                </span>
              )}
              {item.priority === "high" && !done && (
                <span className={cn("text-[10px] font-medium tracking-wider uppercase px-2 py-0.5 rounded-full", prio.color)}>
                  High
                </span>
              )}
              {isPayable && !done && (
                <span className="text-[10px] font-medium tracking-wider uppercase px-2 py-0.5 rounded-full bg-tertiary-container text-tertiary-onContainer">
                  Auto-bill
                </span>
              )}
            </div>
          )}
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <button
              data-testid={`item-menu-${item.id}`}
              className="opacity-0 group-hover:opacity-100 p-1.5 rounded-full hover:bg-primary/8 transition-opacity duration-200 ease-md text-surface-onVariant"
              aria-label="Change status"
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-44 p-1 bg-surface rounded-2xl border border-outline-variant shadow-md" align="end">
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <button
                key={k}
                data-testid={`status-${k}-${item.id}`}
                onClick={() => updateItem(item.id, { status: k })}
                className={cn(
                  "w-full text-left px-3 py-2 rounded-full text-sm hover:bg-primary/8 transition-colors duration-200 ease-md",
                  item.status === k ? "text-primary font-medium" : "text-surface-on"
                )}
              >
                {v}
              </button>
            ))}
          </PopoverContent>
        </Popover>
      </div>

      <PaymentConfirmDialog
        item={item}
        open={payOpen}
        onOpenChange={setPayOpen}
        onDone={() => updateItem(item.id, { status: "done" })}
      />
    </div>
  );
}
